/**
 * ============================================================
 * EXAM SECURITY MONITOR - Production Grade
 * ============================================================
 * 
 * Enterprise-level security monitoring for online exams:
 * - Tab/Window switching detection
 * - Copy/Paste blocking
 * - DevTools detection
 * - Right-click prevention
 * - Network monitoring
 * - System information tracking
 * - Violation logging with auto-submit
 * 
 * ============================================================
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaShieldAlt,
  FaWifi,
  FaCopy,
  FaEyeSlash,
  FaPuzzlePiece,
  FaDesktop,
  FaCheckCircle,
  FaClock,
  FaMousePointer,
  FaExclamationTriangle,
  FaBan,
  FaInfoCircle,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import "./ExamSecurityMonitor.css";

const VIOLATION_LIMIT = 10; // Auto-submit after 10 violations

export default function ExamSecurityMonitor({ onViolationLimitReached }) {
  const [securityLogs, setSecurityLogs] = useState([]);
  const [violationCount, setViolationCount] = useState(0);
  const [securityStatus, setSecurityStatus] = useState("secure");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [systemInfo, setSystemInfo] = useState(null);
  const [hasExtensions, setHasExtensions] = useState(false);
  const [isTabActive, setIsTabActive] = useState(true);
  const [isExamBlocked, setIsExamBlocked] = useState(false);
  const [blockedReason, setBlockedReason] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showAlert, setShowAlert] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const logIdCounter = useRef(0);
  const lastFocusTime = useRef(Date.now());
  const violationCountRef = useRef(0);

  const generateLogId = () => `log-${++logIdCounter.current}-${Date.now()}`;

  const addSecurityLog = useCallback(
    (type, message, icon) => {
      const newLog = {
        id: generateLogId(),
        type,
        message,
        timestamp: new Date(),
        icon,
      };
      setSecurityLogs((prev) => [newLog, ...prev.slice(0, 9)]); // Keep last 10 logs

      if (type === "violation") {
        setViolationCount((prev) => {
          const newCount = prev + 1;
          violationCountRef.current = newCount;
          
          // Show warning at 3 violations
          if (newCount === 3) {
            setShowAlert("⚠️ Warning: Multiple violations detected. Your exam may be terminated if violations continue.");
            setTimeout(() => setShowAlert(null), 8000);
          }
          
          // Auto-submit at violation limit
          if (newCount >= VIOLATION_LIMIT) {
            setIsExamBlocked(true);
            setBlockedReason("You have exceeded the allowed number of violations. Your exam will be auto-submitted.");
            if (onViolationLimitReached) {
              onViolationLimitReached();
            }
          }
          
          return newCount;
        });
        setSecurityStatus("violation");
        setIsCollapsed(false); // Auto-expand on violation
      } else if (type === "warning" && securityStatus === "secure") {
        setSecurityStatus("warning");
      }
    },
    [securityStatus, onViolationLimitReached]
  );

  const detectSystemInfo = useCallback(() => {
    const ua = navigator.userAgent;
    let browser = "Unknown";
    let os = "Unknown";

    if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
    else if (ua.includes("Firefox")) browser = "Firefox";
    else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
    else if (ua.includes("Edg")) browser = "Edge";
    else if (ua.includes("Opera") || ua.includes("OPR")) browser = "Opera";

    if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac")) os = "macOS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (ua.includes("iOS")) os = "iOS";

    const info = {
      browser,
      os,
      language: navigator.language,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      ram: navigator.deviceMemory ? `${navigator.deviceMemory}GB` : "Unknown",
      cpuCores: navigator.hardwareConcurrency || 0,
      platform: navigator.platform,
    };

    setSystemInfo(info);
    addSecurityLog("info", `System: ${browser} on ${os}`, <FaDesktop />);
  }, [addSecurityLog]);

  const checkExtensions = useCallback(() => {
    if (typeof window !== "undefined" && window.chrome && window.chrome.runtime) {
      try {
        if (window.chrome.runtime.getManifest) {
          setHasExtensions(true);
          addSecurityLog(
            "warning",
            "Browser extensions detected",
            <FaPuzzlePiece />
          );
        }
      } catch (err) {
        // Try alternative detection
        const img = new Image();
        img.onload = () => {
          setHasExtensions(true);
          addSecurityLog(
            "warning",
            "Browser extensions may be active",
            <FaPuzzlePiece />
          );
        };
        img.onerror = () => setHasExtensions(false);
        img.src =
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
      }
    } else {
      setHasExtensions(false);
    }
  }, [addSecurityLog]);

  // Network monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      addSecurityLog("info", "Network connection restored", <FaWifi />);
    };
    const handleOffline = () => {
      setIsOnline(false);
      addSecurityLog("warning", "Network connection lost", <FaExclamationTriangle />);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [addSecurityLog]);

  // Tab/Window visibility monitoring
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsTabActive(false);
        lastFocusTime.current = Date.now();
        addSecurityLog("violation", "Tab switched or window minimized", <FaEyeSlash />);
      } else {
        setIsTabActive(true);
        const away = Date.now() - lastFocusTime.current;
        if (away > 2000) {
          addSecurityLog(
            "warning",
            `Returned to exam (away ${Math.round(away / 1000)}s)`,
            <FaCheckCircle />
          );
        }
      }
    };

    const handleBlur = () => {
      lastFocusTime.current = Date.now();
      addSecurityLog("violation", "Window lost focus", <FaEyeSlash />);
    };

    const handleFocus = () => {
      const away = Date.now() - lastFocusTime.current;
      if (away > 1000) {
        addSecurityLog("info", "Window regained focus", <FaCheckCircle />);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [addSecurityLog]);

  // Keyboard shortcuts & DevTools detection
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      if (
        e.key === "F12" ||
        (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key)) ||
        (e.ctrlKey && e.key === "u") ||
        (e.ctrlKey && e.key === "U")
      ) {
        e.preventDefault();
        e.stopPropagation();
        addSecurityLog("violation", "Developer tools access blocked", <FaBan />);
        return false;
      }

      // Block copy/paste/cut/select all
      if (e.ctrlKey && ["c", "v", "x", "a", "C", "V", "X", "A"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        let action = "keyboard shortcut";
        if (e.key.toLowerCase() === "c") action = "Copy attempt";
        else if (e.key.toLowerCase() === "v") action = "Paste attempt";
        else if (e.key.toLowerCase() === "x") action = "Cut attempt";
        else if (e.key.toLowerCase() === "a") action = "Select all attempt";

        addSecurityLog("violation", `Blocked ${action}`, <FaCopy />);
        return false;
      }
    };

    // Right-click prevention
    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      addSecurityLog("violation", "Right-click blocked", <FaMousePointer />);
      return false;
    };

    // Clipboard events
    const handleClipboard = (e, type) => {
      e.preventDefault();
      e.stopPropagation();
      addSecurityLog("violation", `${type} operation blocked`, <FaCopy />);
      return false;
    };

    // Prevent text selection (optional - can be too aggressive)
    const handleSelectStart = (e) => {
      // Allow selection for coding questions, but log it
      if (e.target.closest('.monaco-editor') || e.target.closest('textarea') || e.target.closest('input')) {
        return; // Allow in editor/input fields
      }
      // For other areas, we can optionally prevent selection
      // e.preventDefault();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("copy", (e) => handleClipboard(e, "Copy"), true);
    document.addEventListener("paste", (e) => handleClipboard(e, "Paste"), true);
    document.addEventListener("cut", (e) => handleClipboard(e, "Cut"), true);
    document.addEventListener("selectstart", handleSelectStart, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("copy", (e) => handleClipboard(e, "Copy"), true);
      document.removeEventListener("paste", (e) => handleClipboard(e, "Paste"), true);
      document.removeEventListener("cut", (e) => handleClipboard(e, "Cut"), true);
      document.removeEventListener("selectstart", handleSelectStart, true);
    };
  }, [addSecurityLog]);

  // Idle detection
  useEffect(() => {
    let idleTimer;
    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        addSecurityLog("warning", "User inactive for 30 seconds", <FaExclamationTriangle />);
      }, 30000);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    resetTimer();

    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      clearTimeout(idleTimer);
    };
  }, [addSecurityLog]);

  // Initialize
  useEffect(() => {
    detectSystemInfo();
    checkExtensions();
    addSecurityLog("info", "Security monitoring active", <FaShieldAlt />);
  }, [detectSystemInfo, checkExtensions, addSecurityLog]);

  // Auto-collapse after 5 seconds if no violations
  useEffect(() => {
    if (!isCollapsed && securityStatus !== "violation" && violationCount === 0) {
      const timer = setTimeout(() => setIsCollapsed(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed, securityStatus, violationCount]);

  const getSecurityStatusConfig = () => {
    switch (securityStatus) {
      case "secure":
        return { 
          color: "#22c55e", 
          bgColor: "#dcfce7",
          text: "Secure", 
          icon: <FaCheckCircle />,
          label: "All Clear"
        };
      case "warning":
        return { 
          color: "#eab308", 
          bgColor: "#fef9c3",
          text: "Warning", 
          icon: <FaExclamationTriangle />,
          label: "Caution"
        };
      case "violation":
        return { 
          color: "#ef4444", 
          bgColor: "#fee2e2",
          text: "Violation", 
          icon: <FaBan />,
          label: "Alert"
        };
      default:
        return { 
          color: "#6b7280", 
          bgColor: "#f3f4f6",
          text: "Unknown", 
          icon: <FaInfoCircle />,
          label: "Unknown"
        };
    }
  };

  const statusConfig = getSecurityStatusConfig();
  const violationPercentage = (violationCount / VIOLATION_LIMIT) * 100;

  return (
    <>
      <div className="esm-container">
        {/* Collapsed Floating Button */}
        {isCollapsed ? (
          <button
            className="esm-floating-btn"
            onClick={() => setIsCollapsed(false)}
            style={{ backgroundColor: statusConfig.color }}
            title={`Security Status: ${statusConfig.text} (${violationCount} violations)`}
          >
            <FaShieldAlt />
            {violationCount > 0 && (
              <span className="esm-badge">{violationCount}</span>
            )}
          </button>
        ) : (
          /* Expanded Monitor Card */
          <div className="esm-card">
            {/* Header */}
            <div className="esm-header">
              <div className="esm-header-left">
                <div 
                  className="esm-status-indicator"
                  style={{ backgroundColor: statusConfig.color }}
                >
                  {statusConfig.icon}
                </div>
                <div>
                  <h3 className="esm-title">Security Monitor</h3>
                  <span className="esm-subtitle">{statusConfig.label}</span>
                </div>
              </div>
              <button 
                className="esm-close-btn" 
                onClick={() => setIsCollapsed(true)}
                title="Minimize"
              >
                <MdClose />
              </button>
            </div>

            {/* Status Bar */}
            <div className="esm-status-bar">
              <div className="esm-status-info">
                <span className="esm-status-text" style={{ color: statusConfig.color }}>
                  {statusConfig.icon} {statusConfig.text}
                </span>
                {!isTabActive && (
                  <span className="esm-tab-warning">
                    <FaEyeSlash /> Tab Inactive
                  </span>
                )}
              </div>
              
              {/* Violation Counter with Progress */}
              <div className="esm-violation-section">
                <div className="esm-violation-header">
                  <span className="esm-violation-label">Violations</span>
                  <span className="esm-violation-count">
                    {violationCount} / {VIOLATION_LIMIT}
                  </span>
                </div>
                <div className="esm-progress-bar">
                  <div 
                    className="esm-progress-fill"
                    style={{ 
                      width: `${violationPercentage}%`,
                      backgroundColor: violationCount >= VIOLATION_LIMIT ? "#ef4444" : 
                                      violationCount >= 7 ? "#f59e0b" : "#3b82f6"
                    }}
                  />
                </div>
                {violationCount >= 7 && violationCount < VIOLATION_LIMIT && (
                  <span className="esm-warning-text">
                    ⚠️ {VIOLATION_LIMIT - violationCount} violations remaining
                  </span>
                )}
              </div>
            </div>

            {/* System Information */}
            {systemInfo && (
              <div className="esm-system-panel">
                <div className="esm-system-header">
                  <FaDesktop /> System Information
                </div>
                <div className="esm-system-grid">
                  <div className="esm-system-item">
                    <span className="esm-system-label">Browser:</span>
                    <span className="esm-system-value">{systemInfo.browser}</span>
                  </div>
                  <div className="esm-system-item">
                    <span className="esm-system-label">OS:</span>
                    <span className="esm-system-value">{systemInfo.os}</span>
                  </div>
                  <div className="esm-system-item">
                    <span className="esm-system-label">Screen:</span>
                    <span className="esm-system-value">{systemInfo.screenSize}</span>
                  </div>
                  <div className="esm-system-item">
                    <span className="esm-system-label">CPU:</span>
                    <span className="esm-system-value">{systemInfo.cpuCores} cores</span>
                  </div>
                  <div className="esm-system-item">
                    <span className="esm-system-label">Network:</span>
                    <span className={`esm-system-value ${isOnline ? "esm-online" : "esm-offline"}`}>
                      <FaWifi /> {isOnline ? "Online" : "Offline"}
                    </span>
                  </div>
                  <div className="esm-system-item">
                    <span className="esm-system-label">Extensions:</span>
                    <span className={`esm-system-value ${hasExtensions ? "esm-warning" : "esm-secure"}`}>
                      {hasExtensions ? <FaPuzzlePiece /> : <FaCheckCircle />}
                      {hasExtensions ? "Detected" : "None"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Security Logs */}
            <div className="esm-logs-section">
              <div className="esm-logs-header">
                <FaClock /> Recent Activity
                <button 
                  className="esm-minimize-btn"
                  onClick={() => setIsMinimized(!isMinimized)}
                >
                  {isMinimized ? <FaChevronDown /> : <FaChevronUp />}
                </button>
              </div>
              
              {!isMinimized && (
                <div className="esm-log-list">
                  {securityLogs.length === 0 ? (
                    <div className="esm-no-log">
                      <FaCheckCircle /> No security events
                    </div>
                  ) : (
                    securityLogs.map((log) => (
                      <div
                        key={log.id}
                        className={`esm-log-item esm-log-${log.type}`}
                      >
                        <div className="esm-log-icon">{log.icon}</div>
                        <div className="esm-log-body">
                          <div className="esm-log-message">{log.message}</div>
                          <div className="esm-log-time">
                            <FaClock /> {log.timestamp.toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Alert Banner */}
        {showAlert && (
          <div className="esm-alert-banner">
            <FaExclamationTriangle />
            <span>{showAlert}</span>
            <button 
              className="esm-alert-close"
              onClick={() => setShowAlert(null)}
            >
              <MdClose />
            </button>
          </div>
        )}

        {/* Exam Block Overlay */}
        {isExamBlocked && (
          <div className="esm-block-overlay">
            <div className="esm-block-card">
              <div className="esm-block-icon-wrapper">
                <FaBan className="esm-block-icon" />
              </div>
              <h2 className="esm-block-title">EXAM BLOCKED</h2>
              <p className="esm-block-message">{blockedReason}</p>
              <div className="esm-block-stats">
                <div className="esm-block-stat">
                  <span className="esm-block-stat-label">Total Violations:</span>
                  <span className="esm-block-stat-value">{violationCount}</span>
                </div>
              </div>
              <button
                className="esm-block-btn"
                onClick={() => {
                  if (onViolationLimitReached) {
                    onViolationLimitReached();
                  } else {
                    window.location.href = "/result";
                  }
                }}
              >
                View Results
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
